/**
 * Read-only parsers for Stream J distribution dashboards.
 */

export type StreamJUnifiedStatus = 'not_started' | 'in_progress' | 'completed' | 'blocked';

export type StreamJPropertyStatus =
  | 'not_started'
  | 'listed'
  | 'pending'
  | 'sold'
  | 'transferred'
  | 'blocked';

export const FIN_PLANNER_FRAMES = ['frame-186', 'frame-187', 'frame-188', 'frame-189', 'frame-190', 'frame-191'] as const;

const SETTLEMENT_CATEGORY_LABELS: Record<string, string> = {
  cash_deposit: 'Cash and Deposit Accounts',
  investment: 'Investment Accounts',
  retirement: 'Retirement Accounts',
  income_source: 'Other Income Sources',
  other_financial: 'Other Financial Assets',
};

const INTENDED_ACTION_LABELS: Record<string, string> = {
  transfer: 'Transfer to beneficiary',
  close: 'Close account',
  claim: 'Claim / collect',
  hold: 'Hold',
};

function asObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export interface StreamJPersonalInventoryRow {
  id: string;
  item: string;
  category: string;
  beneficiaryDisplay: string;
  distributionMethod: string;
  estimatedValue: string;
}

export interface StreamJBusinessRow {
  id: string;
  name: string;
  beneficiaryDisplay: string;
  distributionMethod: string;
  appraisedValue?: number;
  requiresCourt: boolean;
}

export interface StreamJ155RealRow {
  id: string;
  address: string;
  beneficiaryDisplay: string;
  distributionMethod: string;
  appraisedValue?: number;
  requiresCourt: boolean;
  dashboardStatus?: StreamJUnifiedStatus;
}

export interface StreamJ155VehicleRow {
  id: string;
  description: string;
  beneficiaryDisplay: string;
  distributionMethod: string;
  appraisedValue?: number;
  dashboardStatus?: StreamJUnifiedStatus;
}

export interface StreamJFinancialInventoryRow {
  id: string;
  label: string;
  institution: string;
  accountType: string;
  beneficiaryDisplay: string;
  distributionMethod: string;
}

function beneficiaryFromRow(row: Record<string, unknown>): string {
  return str(row.beneficiaryName || row.assigneeName || row.beneficiary || row.beneficiaryId) || '—';
}

function methodFromRow(row: Record<string, unknown>, fallback: string): string {
  const m = str(row.distributionMethod || row.method || row.transferMethodLabel || row.executorDistributionMethod);
  return m.trim() || fallback;
}

function statusFromExecutorProgress(value: unknown): StreamJUnifiedStatus | undefined {
  const status = str(value).toLowerCase().replace(/[_-]+/g, ' ').trim();

  if (status === 'completed' || status === 'complete') return 'completed';
  if (status === 'in progress') return 'in_progress';
  if (status === 'needs review' || status === 'not started') return 'not_started';

  return undefined;
}

export function extractPersonalPropertyFrom160(data: Record<string, unknown> | null | undefined): StreamJPersonalInventoryRow[] {
  if (!data) return [];

  // New unified format (items[]) → current legacy (personalItems[]) → old legacy (personal[])
  const list = Array.isArray(data.items) && data.items.length
    ? data.items
    : Array.isArray(data.personalItems) && (data.personalItems as unknown[]).length
      ? data.personalItems
      : Array.isArray(data.personal)
        ? data.personal
        : [];

  const out: StreamJPersonalInventoryRow[] = [];
  let i = 0;
  for (const raw of list) {
    const row = asObj(raw);
    if (!row) continue;
    const id = str(row.id) || `pp-${i++}`;
    const item = str(row.name || row.item || row.description || row.platform);
    if (!item.trim()) continue;
    const category = str(row.type || row.category) || str(row.location) || 'Personal property';
    out.push({
      id,
      item,
      category,
      beneficiaryDisplay: beneficiaryFromRow(row),
      distributionMethod: methodFromRow(row, 'Personal property (inventory)'),
      estimatedValue: str(row.estimatedValue ?? row.value ?? ''),
    });
  }
  return out;
}

export function extractBusinessFrom160(data: Record<string, unknown> | null | undefined): StreamJBusinessRow[] {
  if (!data) return [];
  const list = Array.isArray(data.business)
    ? data.business
    : Array.isArray((data as { businessInterests?: unknown[] }).businessInterests)
      ? (data as { businessInterests: unknown[] }).businessInterests
      : [];
  const out: StreamJBusinessRow[] = [];
  let i = 0;
  for (const raw of list) {
    const row = asObj(raw);
    if (!row) continue;
    const id = str(row.id) || `biz-${i++}`;
    const name = str(row.entityName ?? row.name ?? row.description);
    if (!name.trim()) continue;
    out.push({
      id,
      name,
      beneficiaryDisplay: beneficiaryFromRow(row),
      distributionMethod: methodFromRow(row, 'Business interest'),
      appraisedValue: num(row.value ?? row.estimatedValue),
      requiresCourt: Boolean(row.requiresCourt ?? row.courtApproval),
    });
  }
  return out;
}

export function extractRealEstate155(data: Record<string, unknown> | null | undefined): StreamJ155RealRow[] {
  if (!data) return [];
  // "realEstate" is the canonical key; "properties" is the legacy key from old sframe-155 saves
  const list = Array.isArray(data.realEstate)
    ? data.realEstate
    : Array.isArray(data.properties)
      ? data.properties
      : [];
  const out: StreamJ155RealRow[] = [];
  let i = 0;
  for (const raw of list) {
    const row = asObj(raw);
    if (!row) continue;
    const id = str(row.id) || `re-${i++}`;
    const address = str(row.address ?? row.description ?? row.label ?? 'Real property');
    if (!address.trim()) continue;
    out.push({
      id,
      address,
      beneficiaryDisplay: beneficiaryFromRow(row),
      distributionMethod: methodFromRow(row, row.intent === 'sell' ? 'Sell, then distribute' : 'Transfer title'),
      appraisedValue: num(row.appraisedValue) ?? num(row.valueEstimate) ?? num(row.estimatedValue),
      requiresCourt: Boolean(row.requiresCourt ?? row.courtApproval),
      dashboardStatus: statusFromExecutorProgress(row.executorProgressStatus),
    });
  }
  return out;
}

export function extractVehicles155(data: Record<string, unknown> | null | undefined): StreamJ155VehicleRow[] {
  if (!data) return [];
  // "vehicles" is the canonical key; "vehicleCards" is the legacy key from old sframe-155 saves
  const list = Array.isArray(data.vehicles)
    ? data.vehicles
    : Array.isArray(data.vehicleCards)
      ? data.vehicleCards
      : [];
  const out: StreamJ155VehicleRow[] = [];
  let i = 0;
  for (const raw of list) {
    const row = asObj(raw);
    if (!row) continue;
    const id = str(row.id) || `veh-${i++}`;
    const description = str(row.description ?? row.makeModel ?? row.vin ?? 'Vehicle');
    if (!description.trim()) continue;
    out.push({
      id,
      description,
      beneficiaryDisplay: beneficiaryFromRow(row),
      distributionMethod: methodFromRow(row, row.intent === 'sell' ? 'Sell, then distribute' : 'Transfer title'),
      appraisedValue:
        num(row.estimatedValue) ?? num(row.value) ?? num((row as Record<string, unknown>).valueEstimate as unknown),
      dashboardStatus: statusFromExecutorProgress(row.executorProgressStatus),
    });
  }
  return out;
}

function walkPlannerAccounts(blob: Record<string, unknown> | null, frameKey: string, startIdx: number) {
  const rows: StreamJFinancialInventoryRow[] = [];
  let idx = startIdx;
  const pushList = (
    arr: unknown[],
    mapFn: (a: Record<string, unknown>) => Omit<StreamJFinancialInventoryRow, 'id'>
  ) => {
    for (const item of arr) {
      const a = asObj(item);
      if (!a) continue;
      const m = mapFn(a);
      if (!m.label.trim() && !m.institution.trim()) continue;
      rows.push({ id: `planner-${frameKey}-${idx++}`, ...m });
    }
    return idx;
  };

  if (!blob) return { rows, nextIdx: idx };

  idx = pushList(Array.isArray(blob.cashAccounts) ? blob.cashAccounts : [], (a) => ({
    label: str(a.name || 'Cash account'),
    institution: str(a.institution),
    accountType: 'Cash',
    beneficiaryDisplay: '—',
    distributionMethod: 'See estate plan',
  }));

  idx = pushList(Array.isArray(blob.investmentAccounts) ? blob.investmentAccounts : [], (a) => ({
    label: str(a.name || 'Investment account'),
    institution: str(a.institution),
    accountType: 'Investment',
    beneficiaryDisplay: '—',
    distributionMethod: 'See estate plan',
  }));

  idx = pushList(Array.isArray(blob.retirementAccounts) ? blob.retirementAccounts : [], (a) => ({
    label: str(a.name || 'Retirement'),
    institution: str(a.institution),
    accountType: 'Retirement',
    beneficiaryDisplay: '—',
    distributionMethod: 'See estate plan',
  }));

  idx = pushList(Array.isArray(blob.otherFinancialAssets) ? blob.otherFinancialAssets : [], (a) => ({
    label: str(a.name || 'Policy / asset'),
    institution: '',
    accountType: str(a.type || 'Insurance / annuity'),
    beneficiaryDisplay: '—',
    distributionMethod: 'See estate plan',
  }));

  return { rows, nextIdx: idx };
}

export function extractFinancialAccountsFromSettlementData(data: Record<string, unknown> | null | undefined): StreamJFinancialInventoryRow[] {
  if (!data) return [];
  const candidates = ['accounts', 'financialAccounts', 'executorAccounts', 'items', 'rows'];
  let list: unknown[] = [];
  for (const key of candidates) {
    const v = data[key];
    if (Array.isArray(v) && v.length) {
      list = v;
      break;
    }
  }
  const out: StreamJFinancialInventoryRow[] = [];
  let i = 0;
  for (const raw of list) {
    const row = asObj(raw);
    if (!row) continue;
    const id = str(row.id) || `fin-${i++}`;
    const institution = str(row.institutionName ?? row.bankName ?? row.institution ?? row.custodian ?? 'Financial institution');
    const label =
      str(row.accountNickname ?? row.accountName ?? row.name ?? row.label) ||
      str(row.accountTypeLabel ?? row.accountType ?? '') ||
      'Account';
    const rawCategory = str(row.category ?? '');
    const categoryLabel = SETTLEMENT_CATEGORY_LABELS[rawCategory] ?? rawCategory;
    const acctType = str(row.accountTypeLabel ?? row.accountType ?? row.type ?? row.subtype ?? '') || categoryLabel;
    const lastFourRaw = row.maskedAccountNumber ?? row.lastFour ?? '';
    const suffix = String(lastFourRaw).replace(/\D/g, '').slice(-4);
    const labelWithSuffix = suffix ? `${label} (...${suffix})` : label;
    const intendedLabel = INTENDED_ACTION_LABELS[str(row.intendedAction)] ?? '';
    out.push({
      id,
      label: labelWithSuffix,
      institution,
      accountType: acctType || 'Financial account',
      beneficiaryDisplay: beneficiaryFromRow(row),
      distributionMethod: methodFromRow(row, intendedLabel || 'Beneficiary designation / probate'),
    });
  }
  return out;
}

export async function plannerFinancialFallbackRows(loadPlannerData: (id: string) => Promise<Record<string, unknown> | null>): Promise<
  StreamJFinancialInventoryRow[]
> {
  const merged: StreamJFinancialInventoryRow[] = [];
  let idx = 0;
  for (const frame of FIN_PLANNER_FRAMES) {
    const blob = await loadPlannerData(frame);
    const { rows, nextIdx } = walkPlannerAccounts(blob, frame, idx);
    merged.push(...rows);
    idx = nextIdx;
  }
  return merged;
}
